/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import { StrategySchemaV2Provider } from '../../context/StrategySchemaV2Context';
import { useStrategySchemaV2 } from '../../hooks/useStrategySchemaV2';
import SchemaNodePalette from '../../components/bots/sectioned-builder/SchemaNodePalette';
import ComparisonEditor from '../../components/bots/sectioned-builder/ComparisonEditor';
import SectionsEditor from '../../components/bots/sectioned-builder/SectionsEditor';
import NodeInspector from '../../components/bots/sectioned-builder/NodeInspector';
import GraphCanvas from '../../components/bots/sectioned-builder/GraphCanvas';
import BuilderSteps from '../../components/bots/sectioned-builder/BuilderSteps';
import SectionsSummary from '../../components/bots/sectioned-builder/SectionsSummary';
import ValidationPanel from '../../components/bots/sectioned-builder/ValidationPanel';
import { useStrategyValidation } from '../../hooks/useStrategyValidation';
import { createStrategyDraft, patchStrategyDraft, getStrategyDraft } from '../../api/botsApi';
import SchemaStatusBanner from '../../components/bots/sectioned-builder/SchemaStatusBanner';

// New unified graph state: comparison nodes reside inside nodes[] with type==='comparison'
function useGraphState() {
  const [nodes, setNodes] = useState([]); // indicator / arithmetic / comparison / predicate
  const [sections, setSections] = useState({
    ENTRY_LONG: { root: { type: 'group', op: 'AND', children: [] } },
    ENTRY_SHORT: { root: { type: 'group', op: 'AND', children: [] } },
    EXIT_LONG: { root: { type: 'group', op: 'AND', children: [] } },
    EXIT_SHORT: { root: { type: 'group', op: 'AND', children: [] } },
  });
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const api = useMemo(() => ({
    nodes,
    setNodes,
    sections,
    setSections,
    selectedNodeId,
    setSelectedNodeId,
  }), [nodes, sections, selectedNodeId]);
  return api;
}

// Inner content that consumes schema context
const StrategyBuilderV2Content = () => {
  const { botId, versionId } = useParams();
  const graph = useGraphState();
  const { schemaHash } = useStrategySchemaV2() || {};
  const [activeComparisonId, setActiveComparisonId] = useState(null);

  // Derived list of comparison nodes (legacy compatibility for validation/editor)
  const comparisonNodes = useMemo(() => graph.nodes.filter(n => n.type === 'comparison'), [graph.nodes]);

  const availableSeries = useMemo(() => {
    return graph.nodes
      .filter(n => n.type !== 'comparison')
      .flatMap(n => {
      const res = [];
      if (Array.isArray(n.outputs) && n.outputs.length > 0) {
        if (n.outputs.length === 1) {
          // single output: keep simple id reference
          res.push({ value: n.id, label: `${n.id}` });
        } else {
          // multi outputs: add alias for default pointing to defaultOutput
          if (n.defaultOutput && n.outputs.includes(n.defaultOutput)) {
            res.push({ value: n.id, label: `${n.id} (${n.defaultOutput})` });
          }
          res.push(...n.outputs.map(o => ({ value: `${n.id}:${o}`, label: `${n.id}.${o}${n.defaultOutput===o ? ' *' : ''}` })));
        }
      } else {
        res.push({ value: n.id, label: n.id });
      }
      return res;
    });
  }, [graph.nodes]);
  const activeComparison = comparisonNodes.find(c => c.id === activeComparisonId) || null;

  const validation = useStrategyValidation(graph.nodes, comparisonNodes, graph.sections);
  const [draftId, setDraftId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [dirty, setDirty] = useState(false);

  // mark dirty when graph changes
  React.useEffect(()=>{ setDirty(true); }, [graph.nodes, graph.sections]);

  const handleSave = async () => {
    if (!validation.summary.isValid) return; // gate
    const graphJson = serialize();
    const payload = {
      bot_id: botId,
      name: draftName || undefined,
      graph_json: graphJson,
    };
    setIsSaving(true);
    try {
      if (draftId) {
        await patchStrategyDraft(draftId, payload);
      } else {
        const res = await createStrategyDraft(payload);
        const newId = res?.id || res?.data?.id || res?.data?.pk || res?.data?.uuid;
        if (newId) setDraftId(newId);
      }
      setDirty(false);
    } catch (e) {
      console.error('Save failed', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoad = async () => {
    const id = window.prompt('Enter draft id to load');
    if (!id) return;
    setIsLoadingDraft(true);
    try {
      const res = await getStrategyDraft(id);
      const data = res.data || res;
      const graphObj = data.graph_json || data.graph || data.payload || {};
      const { nodes = [], comparisons = [], sections = {} } = graphObj;
      // Migration: wrap legacy sections lacking root
      const migratedSections = Object.fromEntries(Object.entries(sections).map(([k,v]) => {
        if (!v) return [k, { root: { type:'group', op:'AND', children:[] } }];
        if (v.root) return [k, v];
        // legacy group object directly
        return [k, { root: v }];
      }));
      // Migration: turn legacy comparisons array into comparison nodes if present
      let mergedNodes = [...nodes];
      if (Array.isArray(comparisons) && comparisons.length) {
        comparisons.forEach(c => {
          // legacy shape: {id,lhsRef,operator,rhsKind,rhsRef?,rhsValue?}
            const cmpId = c.id || mergedNodes.filter(n=>n.type==='comparison').length+1;
            if (!mergedNodes.some(n => n.type==='comparison' && n.id === cmpId)) {
              mergedNodes.push({
                id: cmpId,
                type: 'comparison',
                lhs: c.lhsRef || '',
                operator: c.operator || '>',
                rhs: c.rhsKind === 'const' ? (c.rhsValue ?? 0) : (c.rhsRef || '')
              });
            }
        });
      }
      graph.setNodes(mergedNodes);
      graph.setSections(migratedSections);
      setDraftId(id);
      setDraftName(data.name || '');
      setDirty(false);
    } catch (e) {
      console.error('Load failed', e);
    } finally {
      setIsLoadingDraft(false);
    }
  };

  const handleNew = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    graph.setNodes([]);
    graph.setSections({
      ENTRY_LONG: { root: { type: 'group', op: 'AND', children: [] } },
      ENTRY_SHORT: { root: { type: 'group', op: 'AND', children: [] } },
      EXIT_LONG: { root: { type: 'group', op: 'AND', children: [] } },
      EXIT_SHORT: { root: { type: 'group', op: 'AND', children: [] } },
    });
    setDraftId(null);
    setDraftName('');
    setDirty(false);
  };

  const serialize = () => {
    // Normalize inline raw price/volume operands if found inside comparison nodes
    const normOperand = (v) => {
      if (!v) return v;
      if (typeof v === 'object' && v.type === 'price') {
        // prefer 'field' key per volume guide
        const { field, source, timeframe } = v;
        const fld = field || source; // accept legacy 'source'
        const out = { type:'price', field: fld };
        if (timeframe) out.timeframe = timeframe;
        return out;
      }
      return v; // number|string
    };
    const nodes = graph.nodes.map(n => {
      if (n.type === 'comparison') {
        return { id: n.id, type: 'comparison', lhs: normOperand(n.lhs), operator: n.operator, rhs: normOperand(n.rhs) };
      }
      return { id: n.id, type: n.kind || n.type || 'indicator', name: n.name, params: n.params, outputs: n.outputs, defaultOutput: n.defaultOutput, x: n.x ?? 0, y: n.y ?? 0 };
    });
    return {
      nodes,
      sections: graph.sections,
      meta: { schemaHash: schemaHash || 'unknown' },
      validation: validation.summary,
    };
  };

  return (
    <PageLayout
      title="Strategy Builder V2 (Experimental)"
      backButton={{
        label: versionId ? 'Back to Version' : 'Back to Bot',
        to: versionId ? `/bots/${botId}/versions/${versionId}` : `/bots/${botId}`,
        preferHistory: false,
        hideLabelOnMobile: true,
      }}
    >
      <div className="space-y-6">
        <IntroPanel />
        <div id="steps" />
        <BuilderSteps
          nodes={graph.nodes}
          comparisons={comparisonNodes}
          sections={graph.sections}
          validationSummary={validation.summary}
          dirty={dirty}
        />
        <SchemaStatusBanner />
        <div className="border rounded-md p-3 bg-card text-[11px] flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-textSecondary">Draft Name</label>
            <input
              className="h-8 text-xs rounded border bg-background px-2"
              value={draftName}
              onChange={(e)=>setDraftName(e.target.value)}
              placeholder="(optional)"
            />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={handleNew}
              className="h-8 px-3 rounded border bg-muted/40 hover:bg-muted text-xs"
            >New</button>
            <button
              type="button"
              onClick={handleLoad}
              className="h-8 px-3 rounded border bg-muted/40 hover:bg-muted text-xs"
              disabled={isLoadingDraft}
            >{isLoadingDraft? 'Loading...' : 'Load'}</button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!validation.summary.isValid || isSaving || !dirty}
              className={`h-8 px-3 rounded border text-xs ${(!validation.summary.isValid || isSaving || !dirty) ? 'bg-muted/20 text-textSecondary cursor-not-allowed' : 'bg-primary/30 hover:bg-primary/40 border-primary/50'}`}
            >{isSaving? 'Saving...' : draftId ? 'Save' : 'Create'}</button>
            {draftId && (
              <span className="text-[10px] text-textSecondary">ID: {draftId}{dirty && ' *'}</span>
            )}
            {!draftId && dirty && <span className="text-[10px] text-amber-600">* unsaved</span>}
          </div>
          <div className="basis-full flex items-center gap-2 text-[10px] mt-1">
            <span className={validation.summary.isValid ? 'text-green-600' : 'text-red-600'}>{validation.summary.isValid ? 'Valid' : 'Fix errors before saving'}</span>
            <span className="text-textSecondary">{validation.summary.errorCount} errors / {validation.summary.warningCount} warnings</span>
            {dirty && <span className="text-amber-600">Dirty</span>}
          </div>
        </div>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="md:col-span-2 space-y-3">
            <div id="node-palette" className="border rounded-md p-3 bg-card">
              <SchemaNodePalette onAdd={(desc) => {
                const id = `n${graph.nodes.length + 1}`;
                const paramsObj = (desc.params||[]).reduce((acc,p)=>{acc[p.name]=p.default ?? '';return acc;},{} );
                const defaultOutput = Array.isArray(desc.outputs) && desc.outputs.length ? desc.outputs[0] : null;
                const yBase = graph.nodes.length * 90; // rough vertical stacking for now
                graph.setNodes([...graph.nodes, { id, ...desc, params: paramsObj, defaultOutput, x: 40, y: yBase }]);
              }} />
            </div>
            <div className="border rounded-md p-3 bg-card">
              <h3 className="text-xs font-semibold tracking-wide mb-2 text-textSecondary">Nodes (placeholder)</h3>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wide text-textSecondary">Graph</span>
                  <button
                    type="button"
                    className="px-2 py-0.5 rounded bg-muted text-[10px]"
                    onClick={()=>{
                      // simple auto layout: grid 3 cols
                      const COLS = 3;
                      const GAP_X = 160; const GAP_Y = 110;
                      graph.setNodes(graph.nodes.map((n,i)=> ({ ...n, x: 40 + (i % COLS)*GAP_X, y: 20 + Math.floor(i/COLS)*GAP_Y })));
                    }}
                  >Auto Layout</button>
                </div>
                <GraphCanvas
                  nodes={graph.nodes}
                  selectedId={graph.selectedNodeId}
                  onSelect={graph.setSelectedNodeId}
                  onMove={(id,x,y)=>graph.setNodes(graph.nodes.map(n=>n.id===id? { ...n, x, y }: n))}
                  activeComparison={activeComparison}
                  validation={validation}
                />
              </div>
              {graph.nodes.length === 0 && (
                <div className="text-[11px] text-textSecondary">No nodes yet.</div>
              )}
              {graph.nodes.length > 0 && (
                <ul className="space-y-1 max-h-48 overflow-auto pr-1 text-[11px]">
                  {graph.nodes.map(n => {
                    const active = graph.selectedNodeId === n.id;
                    return (
                      <li key={n.id} className={`flex items-center justify-between gap-2 px-2 py-1 rounded border ${active ? 'bg-primary/20 border-primary/50' : 'bg-muted/40 border-transparent hover:bg-muted/60'}`}>
                        <button
                          type="button"
                          onClick={() => graph.setSelectedNodeId(n.id)}
                          className="flex-1 text-left truncate"
                          title={n.name}
                        >{n.id}: {n.name}</button>
                        <button
                          type="button"
                          onClick={() => {
                            graph.setNodes(graph.nodes.filter(x=>x.id!==n.id));
                            if (graph.selectedNodeId === n.id) graph.setSelectedNodeId(null);
                          }}
                          className="text-red-500 hover:text-red-600"
                          aria-label="Remove node"
                        >×</button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <div className="md:col-span-3 space-y-3">
            <div id="sections" className="border rounded-md p-3 bg-card flex flex-col gap-3">
              <h3 className="text-xs font-semibold tracking-wide text-textSecondary">Sections Logic</h3>
              <SectionsSummary sections={graph.sections} />
            </div>
            <div id="validation" className="border rounded-md p-3 bg-card space-y-4">
              <h3 className="text-xs font-semibold tracking-wide mb-2 text-textSecondary">Inspector (placeholder)</h3>
              <NodeInspector
                node={graph.nodes.find(n=>n.id===graph.selectedNodeId)}
                onChange={(updated)=>graph.setNodes(graph.nodes.map(n=>n.id===updated.id? updated : n))}
                allNodes={graph.nodes}
                allComparisons={comparisonNodes}
                allSections={graph.sections}
              />
              {graph.selectedNodeId && (
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    className="px-2 py-1 rounded bg-muted text-[10px] border"
                    onClick={()=>{
                      const original = graph.nodes.find(n=>n.id===graph.selectedNodeId);
                      if (!original) return;
                      let idx = graph.nodes.length + 1;
                      let newId;
                      do { newId = `n${idx++}`; } while (graph.nodes.some(n=>n.id===newId));
                      const clone = { ...original, id:newId, x: (original.x||40) + 40, y: (original.y||0) + 40 };
                      graph.setNodes([...graph.nodes, clone]);
                    }}
                  >Duplicate Node</button>
                </div>
              )}
              <ValidationPanel validation={validation} />
              <div id="comparisons" className="mt-4 border-t pt-2 space-y-2">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-semibold">Comparisons</span>
                  <button
                    type="button"
                    className="px-2 py-0.5 rounded bg-muted text-[10px]"
                    onClick={()=>{
                      // Generate next comparison id (numeric) unique among comparison nodes
                      const existing = graph.nodes.filter(n=>n.type==='comparison').map(n=>n.id);
                      let nextId = existing.length + 1;
                      while (existing.includes(nextId)) nextId += 1;
                      graph.setNodes([...graph.nodes, { id: nextId, type:'comparison', lhs:'', operator:'>', rhs:0 }]);
                      setActiveComparisonId(nextId);
                    }}
                  >+ Add</button>
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  <button
                    type="button"
                    className="px-2 py-0.5 rounded bg-primary/20 border border-primary/40 text-[10px] hover:bg-primary/30"
                    title="Create Relative Volume comparison: (volume / SMA(volume,20)) > 1.5"
                    onClick={() => {
                      const nodes = [...graph.nodes];
                      // Find or create SMA(volume,20)
                      const smaExisting = nodes.find(n => n.type==='indicator' && (n.name||'').toUpperCase()==='SMA' && n.params && n.params.length===20 && (n.params.source==='volume' || n.params.source==='volume'));
                      let smaId = smaExisting?.id;
                      if (!smaId) {
                        smaId = `n${nodes.length+1}`;
                        nodes.push({ id: smaId, type:'indicator', name:'SMA', params:{ length:20, source:'volume' }, outputs:['sma'], defaultOutput:'sma', x:40, y: (nodes.length)*90 });
                      }
                      // Create arithmetic node
                      const ratioId = `n${nodes.length+1}`;
                      nodes.push({ id: ratioId, type:'arithmetic', tokens:[
                        { kind:'source', ref:'volume' },
                        { kind:'op', value:'/' },
                        { kind:'source', ref:`${smaId}:sma` },
                      ], outputs:['value'], defaultOutput:'value', x:120, y:(nodes.length-1)*90 });
                      // Comparison
                      const cmpExisting = nodes.filter(n=>n.type==='comparison').map(n=>n.id);
                      let cmpId = cmpExisting.length + 1;
                      while (cmpExisting.includes(cmpId)) cmpId += 1;
                      nodes.push({ id: cmpId, type:'comparison', lhs: ratioId, operator:'>', rhs:1.5 });
                      // Insert comparison into ENTRY_LONG root group
                      const sec = graph.sections.ENTRY_LONG || { root:{ type:'group', op:'AND', children:[] } };
                      const root = sec.root || sec;
                      if (!root.children.includes(cmpId)) root.children.push(cmpId);
                      graph.setNodes(nodes);
                      graph.setSections({ ...graph.sections, ENTRY_LONG: { root } });
                      setActiveComparisonId(cmpId);
                    }}
                  >Relative Volume &gt; 1.5</button>
                </div>
                {comparisonNodes.length === 0 && <div className="text-[10px] text-textSecondary">No comparisons.</div>}
                {comparisonNodes.length > 0 && (
                  <ul className="flex flex-wrap gap-1">
                    {comparisonNodes.map(c => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={()=>setActiveComparisonId(c.id)}
                          className={`px-2 py-0.5 rounded text-[10px] border ${activeComparisonId===c.id? 'bg-primary/30 border-primary' : 'bg-muted/40 border-borderDefault'}`}
                        >cmp:{c.id}</button>
                        <button
                          type="button"
                          title="Delete comparison"
                          onClick={()=>{
                              graph.setNodes(graph.nodes.filter(x=>!(x.type==='comparison' && x.id===c.id)));
                            if (activeComparisonId===c.id) setActiveComparisonId(null);
                          }}
                          className="ml-1 text-[10px] px-1 rounded bg-red-500/20 text-red-600 hover:bg-red-500/30"
                        >×</button>
                      </li>
                    ))}
                  </ul>
                )}
                {activeComparison && (
                  <ComparisonEditor
                      comparison={activeComparison}
                      onChange={(next)=>graph.setNodes(graph.nodes.map(n => (n.type==='comparison' && n.id===next.id) ? next : n))}
                    availableSeries={availableSeries}
                  />
                )}
              </div>
              <div className="mt-4 border-t pt-2">
                <SectionsEditor
                    sections={graph.sections}
                    setSections={graph.setSections}
                    comparisons={comparisonNodes}
                />
              </div>
              <div id="save-area" className="mt-4 border-t pt-2">
                <div className="flex items-center justify-between mb-1 text-[11px] font-semibold">
                  <span>Serialized</span>
                  <button
                    type="button"
                    className="px-2 py-0.5 rounded bg-muted text-[10px] border"
                    onClick={()=>{
                      try {
                        const json = JSON.stringify(serialize(), null, 2);
                        navigator.clipboard.writeText(json);
                        console.log('Strategy JSON copied');
                      } catch(e) { console.error('Copy failed', e); }
                    }}
                  >Copy JSON</button>
                </div>
                <div className="mb-1 text-[10px] text-textSecondary">{validation.summary.errorCount} errors / {validation.summary.warningCount} warnings • {validation.summary.isValid ? 'Ready' : 'Needs fixes'}</div>
                <pre className="max-h-40 overflow-auto text-[10px] bg-muted/30 p-2 rounded">{JSON.stringify(serialize(), null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const StrategyBuilderV2Page = () => (
  <StrategySchemaV2Provider>
    <StrategyBuilderV2Content />
  </StrategySchemaV2Provider>
);

const IntroPanel = () => (
  <div className="p-4 rounded-lg border bg-card text-sm">
    <p className="font-medium mb-2">Experimental Strategy Builder</p>
    <p>This implementation will migrate to a schema-driven node graph. Current view is a scaffold.</p>
    <ul className="list-disc ml-5 mt-2 space-y-1 text-textSecondary">
      <li>Fetch & hash backend schema</li>
      <li>Dynamic palette (WIP)</li>
      <li>Graph nodes + sections model</li>
      <li>Round-trip serializer (pending)</li>
    </ul>
  </div>
);

export default StrategyBuilderV2Page;
